'use client';

import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Layout from '@/components/layout/Layout';
import styles from '@/styles/About.module.css';

// Import icons
import { 
  faHeart, 
  faHandshake, 
  faUsers, 
  faLightbulb, 
  faChevronLeft, 
  faChevronRight, 
  faHandHoldingHeart, 
  faUserPlus, 
  faMapMarkerAlt, 
  faArrowRight, 
  faEnvelope,
  faHandHoldingUsd,
  faTasks
} from '@fortawesome/free-solid-svg-icons';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import DonationButton from '@/components/common/DonationButton';

// Dynamically import FontAwesomeIcon to avoid SSR issues
const FontAwesomeIcon = dynamic(
  () => import('@fortawesome/react-fontawesome').then((mod) => mod.FontAwesomeIcon),
  { ssr: false }
);

const CustomNextArrow = (props: any) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  const { className, style, onClick } = props;
  
  if (!mounted) {
    return <div className={className} style={{ ...style, display: 'none' }} />;
  }
  
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', color: 'var(--primary-color)' }}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faChevronRight} size="lg" />
    </div>
  );
};

const CustomPrevArrow = (props: any) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  const { className, style, onClick } = props;
  
  if (!mounted) {
    return <div className={className} style={{ ...style, display: 'none' }} />;
  }
  
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', color: 'var(--primary-color)' }}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faChevronLeft} size="lg" />
    </div>
  );
};

const values = [
  {
    icon: faHeart,
    title: "Equity",
    description: "We believe every young person deserves equal opportunity to achieve their academic and career aspirations."
  },
  {
    icon: faHandshake,
    title: "Empowerment",
    description: "We foster a culture of self-reliance, equipping young adults with the skills and resources to thrive."
  },
  {
    icon: faUsers,
    title: "Community",
    description: "We hold a strong belief in the power of community and invest in its future by supporting its youth."
  },
  {
    icon: faLightbulb,
    title: "Impact",
    description: "We are dedicated to creating a lasting positive change by nurturing the potential of future leaders facing homelessness or underprivileged backgrounds."
  },
  {
    icon: faHandHoldingHeart,
    title: "Holistic Support",
    description: "We provide comprehensive resources that address not just academic needs, but also the social and emotional well-being of young adults."
  }
];

const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  pauseOnHover: true,
  nextArrow: <CustomNextArrow />,
  prevArrow: <CustomPrevArrow />,
  centerMode: true,
  centerPadding: '0',
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        centerMode: false
      }
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        arrows: false
      }
    }
  ]
};

// Component to render FontAwesome icons with client-side only rendering
const ClientSideIcon = ({ icon, title, className = '' }: { icon: any, title?: string, className?: string }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  if (!mounted) {
    return <span className={className} style={{ display: 'inline-block', width: '1em' }} />;
  }
  
  return <FontAwesomeIcon icon={icon} title={title} className={className} />;
};

export default function AboutPage() {
  // Skip to main content link for keyboard users
  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <main id="main-content">
      {/* Hero Section */}
      <section className={styles.hero} aria-labelledby="page-title">
        <div className={styles.container}>
          <h1 id="page-title">About TimeLee Cares</h1>
          <p>Learn about our mission, vision, and the team behind our work</p>
        </div>
      </section>

      {/* Our Story */}
      <section className={styles.story}>
        <div className={styles.container}>
          <div className={styles.storyContent}>
            <div className={styles.storyText}>
              <h2>Our Story</h2>
              <p>
                TimeLee Cares is a non-profit organization dedicated to removing barriers to success
                for unhoused and underserved youth and young adults (14-24 years old) beginning in
                Atlanta, Georgia yet expansive in the United States. We achieve this by providing
                comprehensive support services that empower them to achieve their academic and
                career aspirations. Our core values of equity, empowerment, community, impact, and
                holistic support guide our approach.
              </p>
            </div>
            <div className={styles.storyImage}>
              <img 
                src="/images/university.jpg" 
                alt="Group of diverse students studying together in a university setting" 
                className={styles.image}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className={styles.missionVision}>
        <div className={styles.container}>
          <div className={styles.missionBox}>
            <h2>Our Mission</h2>
            <p>
              TimeLee Cares removes barriers to success for the underserved youth and 
              young adults by providing comprehensive support, including academic and career
              guidance, mentorship, financial literacy education, and secure housing.
            </p>
          </div>
          <div className={styles.visionBox}>
            <h2>Our Vision</h2>
            <p>
              TimeLee Cares Foundation is committed to empowering underserved youth and
              young adults to break the cycle of mental and physical poverty and homelessness. By
              providing comprehensive support services and fostering a culture of empowerment and
              inclusion, we aim to create a positive ripple effect within our community. We invite 
              you to join us in making a difference and invest in our future leaders.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className={styles.values}>
        <div className={styles.container}>
          <h2>Our Core Values</h2>
          {/* Desktop Slider (hidden on mobile) */}
          <div className={styles.desktopSlider}>
            <Slider {...sliderSettings}>
              {values.map((value, index) => (
                <div key={`desktop-${index}`} className={styles.sliderCard}>
                  <div className={styles.valueCard}>
                    <div className={styles.valueIcon}>
                      <FontAwesomeIcon icon={value.icon} />
                    </div>
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          {/* Mobile Grid (hidden on desktop) */}
          <div className={styles.mobileGrid}>
            {values.map((value, index) => (
              <div key={`mobile-${index}`} className={styles.valueCard}>
                <div className={styles.valueIcon}>
                  <FontAwesomeIcon icon={value.icon} />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className={styles.team} aria-labelledby="board-members-heading">
        <div className={styles.container}>
          <h2 id="board-members-heading">Our Board Members</h2>
          <div className={styles.teamGrid}>
            <div className={styles.teamMember}>
              <div className={styles.memberImage}>
                  <img 
                    src="/images/team-member-imgs/BM_EhizeLee.jpeg" 
                    alt="Ehize Lee, Founder and President of TimeLee Cares" 
                    title="Ehize Lee - Founder/President"
                    className={styles.memberPhoto}
                    width="200"
                    height="200"
                    loading="lazy"
                  />
              </div>
              <h3>Ehize Lee</h3>
              <p className={styles.memberTitle}>Founder/President</p>
              <p>Leads and oversees operations, vision, and strategy.</p>
            </div>
            <div className={styles.teamMember}>
              <div className={styles.memberImage}>
                  <img 
                    src="/images/team-member-imgs/BM_SharonBenjamin.jpeg" 
                    alt="Sharon Benjamin, Secretary of TimeLee Cares" 
                    title="Sharon Benjamin - Secretary"
                    className={styles.memberPhoto}
                    width="200"
                    height="200"
                    loading="lazy"
                  />
              </div>
              <h3>Sharon Benjamin</h3>
              <p className={styles.memberTitle}>Secretary</p>
              <p>Manages documentation, communications, and meeting records.</p>
            </div>
            <div className={styles.teamMember}>
              <div className={styles.memberImage}>
                  <img 
                    src="/images/team-member-imgs/BM_SelinaGebrihiwet.jpeg" 
                    alt="Selina Gebrihiwet, Treasurer of TimeLee Cares" 
                    title="Selina Gebrihiwet - Treasurer"
                    className={styles.memberPhoto}
                    width="200"
                    height="200"
                    loading="lazy"
                  />
              </div>
              <h3>Selina Gebrihiwet</h3>
              <p className={styles.memberTitle}>Treasurer</p>
              <p>Oversees financial management, budgeting, and reporting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Our Board of Directors Section */}
      <section className={styles.joinBoard}>
        <div className={styles.container}>
          <h2>Join Our Board of Directors</h2>
          <p className={styles.sectionSubtitle}>
            We're seeking passionate individuals who are committed to making a difference in the lives of young adults. 
            As a board member, you'll help shape our strategic direction and amplify our impact in the community.
          </p>
          
          <div className={styles.teamGrid}>
            {/* Community Relations Chair Position */}
            <div className={styles.positionCard}>
              <div className={styles.positionIcon}>
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <h3 className={styles.positionTitle}>Community Relations Chair</h3>
              <div className={styles.positionStatus}>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Position Open
              </div>
              <p className={styles.positionDescription}>
                Be the voice of TimeLee Cares in the community, building bridges and creating opportunities for collaboration 
                that expand our reach and deepen our impact.
              </p>
              
              <div className={styles.responsibilities}>
                <h4><FontAwesomeIcon icon={faTasks} /> Key Responsibilities</h4>
                <ul>
                  <li>Develop and implement community engagement strategies</li>
                  <li>Establish and nurture partnerships with local organizations</li>
                  <li>Represent TimeLee Cares at community events and meetings</li>
                  <li>Enhance public awareness of our mission and programs</li>
                  <li>Collaborate on program development and community needs assessment</li>
                </ul>
              </div>
              
              <div className={styles.applicationInstructions}>
                <p><strong>To apply, please email your resume and cover letter to:</strong></p>
                <p>info@timeleecares.org</p>
                <p>Subject: Application for Community Relations Chair</p>
              </div>
              
              <a 
                href="mailto:info@timeleecares.org?subject=Application for Community Relations Chair" 
                className="btn btn--primary"
                aria-label="Apply for Community Relations Chair position"
                title="Apply for Community Relations Chair position"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  transition: 'all 0.3s ease',
                  marginTop: '1rem',
                  width: '100%',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--primary-color)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.border = '2px solid var(--primary-color)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                  e.currentTarget.style.border = '2px solid var(--primary-color)';
                }}
              >
                Apply Now <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.9em' }} />
              </a>
            </div>
            
            {/* Funding Chair Position */}
            <div className={styles.positionCard}>
              <div className={styles.positionIcon}>
                <FontAwesomeIcon icon={faHandHoldingUsd} title="Funding Chair" />
              </div>
              <h3 className={styles.positionTitle}>Funding Chair</h3>
              <div className={styles.positionStatus}>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Position Open
              </div>
              <p className={styles.positionDescription}>
                Lead our financial sustainability efforts by developing and implementing comprehensive fundraising 
                strategies that support our mission and growth.
              </p>
              
              <div className={styles.responsibilities}>
                <h4><FontAwesomeIcon icon={faTasks} /> Key Responsibilities</h4>
                <ul>
                  <li>Create and execute annual fundraising plans and campaigns</li>
                  <li>Research and apply for grants and funding opportunities</li>
                  <li>Develop and maintain relationships with donors and sponsors</li>
                  <li>Plan and oversee fundraising events and initiatives</li>
                  <li>Work with the board on financial planning and budgeting</li>
                </ul>
              </div>
              
              <div className={styles.applicationInstructions}>
                <p><strong>To apply, please email your resume and cover letter to:</strong></p>
                <p>info@timeleecares.org</p>
                <p>Subject: Application for Funding Chair</p>
              </div>
              
              <a 
                href="mailto:info@timeleecares.org?subject=Application for Funding Chair" 
                className="btn btn--primary"
                aria-label="Apply for Funding Chair position"
                title="Apply for Funding Chair position"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  transition: 'all 0.3s ease',
                  marginTop: '1rem',
                  width: '100%',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = 'var(--primary-color)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.border = '2px solid var(--primary-color)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                  e.currentTarget.style.border = '2px solid var(--primary-color)';
                }}
              >
                Apply Now <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.9em' }} />
              </a>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className={styles.ctaSection}>
            <div className={styles.ctaContent} style={{ marginTop: '3rem' }}>
              <h3 className={styles.ctaTitle}>Not Sure Which Role Is Right For You?</h3>
              <p className={styles.ctaDescription}>
                We believe that everyone has something valuable to contribute. Whether you're interested in joining our board,
                volunteering your time, or supporting our mission in other ways, we'd love to explore how your unique
                skills and passions can make a difference at TimeLee Cares.
              </p>
              <div className={styles.ctaInstructions}>
                <p><strong>Email us your resume and cover letter to:</strong></p>
                <p>info@timeleecares.org</p>
              </div>
              <div className={styles.ctaButtons} style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <a
                  href="mailto:info@timeleecares.org?subject=Interest in TimeLee Cares Opportunities"
                  className="btn btn--primary"
                  aria-label="Contact us about opportunities"
                  title="Email us to learn more about how you can help"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = 'var(--primary-color)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.border = '2px solid var(--primary-color)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                    e.currentTarget.style.border = '2px solid var(--primary-color)';
                  }}
                >
                  Get in Touch <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '0.9em' }} />
                </a>
                <DonationButton 
                  aria-label="Navigate to donation page" 
                  title="Make a donation to support TimeLee Cares Foundation"
                >
                  Donate Now
                </DonationButton>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>
    </>
  );
}
